import React, { useEffect, useState } from 'react';
import { displayDateUS, getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from '../../../utils/dateUtils';
import { mergeFormDataWithStatusExtras } from '../../../utils/applicationFormPayload';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { getStandardizedSignaturesForGeneralForms } from '../../../utils/formHelpers';
import { medicareNonCoverageSchema } from './general-forms.validation';
import EvaluationInput from '../../EvaluationNew/components/EvaluationInput';
import InputDatePicker from '../../EvaluationNew/components/InputDatePicker';
import EvaluationFormHeader from '../../EvaluationNew/components/EvaluationFormHeader';
import { useApplicationFormLogic } from '../../../hooks/useApplicationFormLogic';
import { request } from '../../../utils/api';
import GeneralFormFooter from './components/GeneralFormFooter';
import CloseButton from '../../../components/Application/CloseButton';
import CorrectionRequestAlert from '../../../components/Application/CorrectionRequestAlert';
import PropTypes from 'prop-types';

const MedicareNonCoverage = ({ onClose }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;
    const [medicareId, setMedicareId] = useState(null);
    const [currentStatus, setCurrentStatus] = useState("");

    const methods = useForm({
        resolver: zodResolver(medicareNonCoverageSchema),
        defaultValues: {
            tenantId: undefined,
            userId: undefined,
            patientId: 10,
            employeeName: "",
            patientName: "",
            patientNumber: "",
            effectiveDate: displayDateUS(getLocalSystemDate()),
            serviceType: "Home Health Services",
            applicantName: "",
            applicantSignature: "",
            applicantSignedAt: "",
            reviewedBySignature: "",
            reviewedBySignedAt: "",
            reviewedBySignedBy: ""
        },
        mode: "onChange"
    });

    const { handleSubmit, watch, reset } = methods;

    const onSubmit = async (data, flag = 'completed', additionalData = {}) => {
        const actualFlag = typeof flag === 'string' ? flag : 'completed';
        try {
            // Ensure both name fields are synced for backend compatibility
            const merged = mergeFormDataWithStatusExtras(data, additionalData);
            const fullName = merged.employeeName || merged.applicantName || userFullName;
            const payload = {
                ...merged,
                employeeName: fullName,
                applicantName: fullName,
                effectiveDate: toIsoDate(merged.effectiveDate),
                applicantSignedAt: toIsoDate(merged.applicantSignedAt),
                reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt),
                flag: actualFlag,
            };

            if (currentStatus === 'completed') {
                payload.flag = 'completed';
            }

            // Handle To Be Corrected comments
            const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
            if (finalComments) {
                payload.comments = finalComments;
            }

            const effectiveId = id || medicareId;
            const url = effectiveId ? `/medicare-non-coverages/${effectiveId}` : '/medicare-non-coverages';
            const method = effectiveId ? 'PUT' : 'POST';

            const response = await request(url, {
                method: method,
                body: JSON.stringify(payload)
            });

            if (response.status === 1) {
                toast.success(response.message || `Medicare Non-Coverage form processed successfully!`);
                if (effectiveId) {
                    // If editing, only navigate away if not a 'save' action
                    if (actualFlag !== 'save') {
                        navigate("/general-forms/list?tab=medicare");
                    }
                } else {
                    // New form, always go back to list
                    navigate("/general-forms/list?tab=medicare");
                }
            } else {
                toast.error(response.message || "Failed to process form!");
            }
        } catch (error) {
            console.error("Error processing form:", error);
            toast.error(`Failed to process form: ${error.message}`);
        }
    };

    const {
        targetUserId,
        isSignatureModalOpen,
        openSignatureModal,
        closeSignatureModal,
        signerName,
        handleSignatureApply,
        handleStatusAction,
        handleSave,
        user: currentUserData,
        isAdmin,
        isReadOnly: hookReadOnly,
    } = useApplicationFormLogic({
        methods,
        applicantNameField: "employeeName",
        applicantSignatureField: "applicantSignature",
        applicantSignatureDateField: "applicantSignedAt",
        adminSignatureField: "reviewedBySignature",
        adminSignatureDateField: "reviewedBySignedAt",
        onClose,
        onSubmit,
        currentStatus
    });

    const isReadOnly = isAdmin ? false : hookReadOnly;

    const userFullName = currentUserData ? `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim() || currentUserData.name || "" : "";

    useEffect(() => {
        const fetchForm = async () => {
            if (!isEditMode) return;
            try {
                const response = await request(`/medicare-non-coverages/${id}`);
                if (response.status === 1 && response.result?.medicareNonCoverage) {
                    const data = response.result.medicareNonCoverage;
                    setCurrentStatus(data.status || "");
                    reset({
                        ...data,
                        effectiveDate: parseIsoDateToMDYUS(data.effectiveDate),
                        applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt),
                        reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt),
                        employeeName: data.employeeName || data.applicantName || userFullName,
                        applicantName: data.applicantName || data.employeeName || userFullName,
                        ...getStandardizedSignaturesForGeneralForms(data, isAdmin)
                    });
                } else {
                    toast.error("Failed to load medicare form");
                }
            } catch (error) {
                console.error("Error loading medicare form:", error);
                toast.error("Error loading medicare form");
            }
        };

        const fetchByUserId = async () => {
            if (!targetUserId || !currentUserData) return;
            try {
                const response = await request(`/medicare-non-coverages?userId=${targetUserId}`);
                if (response.status === 1 && response.result?.items?.length > 0) {
                    const data = response.result.items[0];
                    setMedicareId(data.id);
                    setCurrentStatus(data.status || "");
                    reset({
                        ...data,
                        effectiveDate: parseIsoDateToMDYUS(data.effectiveDate),
                        applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt),
                        reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt),
                        employeeName: data.employeeName || data.applicantName || userFullName,
                        applicantName: data.applicantName || data.employeeName || userFullName,
                        ...getStandardizedSignaturesForGeneralForms(data, isAdmin)
                    });
                }
            } catch (error) {
                console.error("Error fetching medicare form by userId:", error);
            }
        };

        if (isEditMode) {
            fetchForm();
        } else {
            fetchByUserId();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
    }, [isEditMode, id, targetUserId, reset, currentUserData]);

    const onCloseFrom = () => {
        if (isAdmin) {
            navigate("/general-forms/library");
        } else {
            navigate("/general-forms/list?tab=medicare");
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">

            <CloseButton onClose={onCloseFrom} />
            <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-xl overflow-hidden relative">

                <EvaluationFormHeader title="NOTICE OF MEDICARE NON-COVERAGE" />
                <fieldset disabled={isReadOnly} className="border-none p-0 m-0">
                    <div className="p-8 md:p-12">
                        <div className="mb-6 p-4 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
                            <p><strong>Patient Rights:</strong> The Effective Date Coverage of Your Current {watch("serviceType") || "Services"} Will End: {watch("effectiveDate")}</p>
                        </div>

                        <FormProvider {...methods}>
                            <form onSubmit={handleSubmit((data) => onSubmit(data, 'send_to_office'))} className="space-y-6">
                                <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                                    <EvaluationInput name="patientName" label="Patient Name" />
                                    <EvaluationInput name="patientNumber" label="Patient Number" />
                                    <InputDatePicker formatSeparator="/" name="effectiveDate" label="Effective Date" />
                                    <EvaluationInput name="serviceType" label="Service Type" />
                                    <EvaluationInput name="employeeName" label="Employee Name" />
                                </div>

                                <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                                    <p>
                                        YOUR HEALTH CARE PROVIDER HAS DETERMINED THAT MEDICARE PROBABLY WILL NOT PAY FOR YOUR CURRENT HEALTH SERVICES AFTER THE EFFECTIVE DATE INDICATED ABOVE.
                                    </p>
                                    <p>
                                        <strong>YOUR RIGHT TO APPEAL THIS DECISION:</strong> You have the right to an immediate, independent medical review (appeal) of the decision to end Medicare coverage of these services. Your services will continue during the appeal.
                                    </p>
                                    <p>
                                        If you choose to appeal, the independent reviewer will ask for your opinion. The reviewer also will look at your medical records and/or other relevant information. You do not have to pay for this review.
                                    </p>
                                </div>

                                <GeneralFormFooter
                                    methods={methods}
                                    onSave={handleSave}
                                    onSubmit={(data, status) => onSubmit(data, status)}
                                    onStatusUpdate={handleStatusAction}
                                    isSignatureModalOpen={isSignatureModalOpen}
                                    openSignatureModal={openSignatureModal}
                                    closeSignatureModal={closeSignatureModal}
                                    handleSignatureApply={handleSignatureApply}
                                    signerName={signerName}
                                    employeeName={watch("employeeName") || ""}
                                    applicantSignature={watch("applicantSignature") || ""}
                                    applicantSignedAt={watch("applicantSignedAt") || ""}
                                    reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                                    reviewedBySignature={watch("reviewedBySignature") || ""}
                                    reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                                    isReadOnly={isReadOnly}
                                    currentStatus={currentStatus}
                                    submitLabel="Submit & Print"
                                />
                            </form>
                        </FormProvider>
                    </div>
                </fieldset>
            </div>
        </div>
    );
};

MedicareNonCoverage.propTypes = {
    onClose: PropTypes.func
};

export default MedicareNonCoverage;

